// frontend/src/farm/backdropLayout.mjs
// Screen geometry for the farm backdrop. Pure (node --test).
import { mulberry32 } from './rng.mjs';
import { NEAR_OVERLAP, BD } from './backdropAnchors.mjs';

// Left edges of the strip copies that cover [0, viewW). Copy n sits at offset + n*stripW
// and is mirrored when n is odd, so a strip next to its mirror never shows a join.
export function stripCopies(offset, stripW, viewW) {
  const n0 = Math.floor(-offset / stripW);
  const out = [];
  for (let n = n0; offset + n * stripW < viewW; n++) {
    out.push({ x: offset + n * stripW, flip: ((n % 2) + 2) % 2 === 1 });
  }
  return out;
}

// Every repeat of x (period `span`) that lands within [-margin, viewW + margin].
export function repeatPositions(x, span, viewW, margin) {
  const first = x + Math.ceil((-margin - x) / span) * span;
  const out = [];
  for (let p = first; p <= viewW + margin; p += span) out.push(p);
  return out;
}

// Objects of one band spread over a repeating span: shuffled order, random gaps, no overlap.
// x is the object's centre; the last object ends at or before `span`.
export function layoutBand(items, seed, span) {
  const r = mulberry32(seed);
  const order = items.map((it) => ({ it, k: r() })).sort((a, b) => a.k - b.k).map((o) => o.it);
  const total = order.reduce((s, it) => s + it.w, 0);
  if (total > span) throw new Error(`band too full: ${total} > ${span}`);
  const weights = order.map(() => 0.3 + r());
  const wsum = weights.reduce((a, b) => a + b, 0);
  const free = span - total;
  let x = 0;
  return order.map((it, i) => {
    x += (free * weights[i]) / wsum;
    const o = {
      key: it.key, x: x + it.w / 2, w: it.w, scale: it.scale ?? 1,
      flipX: !!it.flippable && r() < 0.5,
      yOffset: Math.round((it.jitterY ?? 0) * r()),
    };
    x += it.w;
    return o;
  });
}

// Normalised sky coordinates (u 0..1 left→right, v 0 = horizon .. 1 = top) to screen px.
// `spread` = how many screen widths u spans; `factor` = parallax share of the pan.
// Lawn rows below the horizon that the distance haze fades over (it must end before the plots).
export const HAZE_CELLS = 2;

export const SKY_FLOOR = 18;
export const SKY_TOP_PAD = 72;   // keeps the sun and moon clear of the top HUD bar at their peak
export function skyToScreen(u, v, { horizonY, viewW, panX, factor, spread }) {
  const range = Math.max(horizonY - SKY_FLOOR - SKY_TOP_PAD, 60);
  return {
    x: viewW / 2 + (u - 0.5) * spread * viewW + panX * factor,
    y: horizonY - SKY_FLOOR - v * range,
  };
}

// Grass tufts along the horizon inside the world: one every 16–28 local px across the lawn,
// 10–40 px below the lawn's top edge, each a random variant.
export function fringeTufts(x0, width, seed, variants) {
  const r = mulberry32(seed);
  const out = [];
  for (let x = x0 + r() * 12; x < x0 + width; x += 16 + r() * 12) {
    out.push({ x, dy: 10 + Math.round(r() * 30), variant: Math.floor(r() * variants) });
  }
  return out;
}

// ---- how the backdrop moves --------------------------------------------------------
// Horizontal parallax per band: the nearer, the faster it slides past.
export const BAND_PARALLAX = { far: 0.12, mid: 0.3, near: 0.6 };

// Clouds hang from the horizon like the rest of the backdrop (a fixed screen height looked
// wrong: the land moved up and down under them). px ABOVE the horizon — high over the hills.
export const CLOUD_ABOVE = { far: { min: 120, max: 175 }, near: { min: 135, max: 200 } };

// Birds fly at their own pace with a gentle horizontal parallax of their own (ignoring the pan
// made them look stuck against the sliding hills), and like the clouds they ride on the
// horizon vertically, `aboveMin`..`aboveMax` px above it.
export const BIRD_FLIGHT = { speedMin: 95, speedMax: 130, factor: 0.2, aboveMin: 85, aboveMax: 140 };

// The fringe tufts live in the world (they zoom and pan with the lawn). Their bases scatter
// over `below`..`below + jitter` local px under the horizon — clear of the hedge's bottom
// edge (24 px below it at zoom 1), so each tuft shows whole, in a loose band, not a line.
export const TUFT_ROW = { below: 48, jitter: 40 };
export const TUFT_H = 24;   // a tuft's display height (local px)

// The near hedge's bottom edge: it reaches NEAR_OVERLAP px past the horizon at zoom 1 and
// scales with the zoom, so it always covers the same strip of lawn. A fixed screen overlap
// swallowed the (zooming) tufts when zoomed out.
export function nearBandBottom(horizonY, zoom) {
  return horizonY + NEAR_OVERLAP * zoom;
}

// A tuft's base, in local px below the horizon: its random fringeTufts() dy (10..40) spread
// evenly over the row's band.
export function tuftOffset(p) {
  return TUFT_ROW.below + ((p.dy - 10) / 30) * TUFT_ROW.jitter;
}

// How big a band draws at a zoom. The near hedge is close enough to scale with the lawn (at
// full size it dwarfed a zoomed-out farm); the fields and mountains are far enough that they
// keep their size.
export function bandScale(name, zoom) {
  return name === 'near' ? zoom : 1;
}

// Band bottoms relative to the horizon. The near hedge reaches HEDGE_REACH px above the
// horizon at zoom 1; the fields start BAND_LIFT.mid px up so they show above it, the ridges
// BAND_LIFT.far px up. The lifts scale with the hedge, so no strip of sky opens between the
// fields and a zoomed-out (smaller) hedge.
export const HEDGE_REACH = 51;
export const BAND_LIFT = { far: 50, mid: 28 };
export function bandBottom(name, horizonY, zoom) {
  if (name === 'near') return nearBandBottom(horizonY, zoom);
  return horizonY - BAND_LIFT[name] * bandScale('near', zoom);
}

// A horizontal position scaled about `cx` (the middle of the screen): a zooming band grows
// and shrinks evenly to both sides instead of from its left edge.
export function scaleAbout(x, cx, s) {
  return cx + (x - cx) * s;
}

// Screen x of a point `o` of the near band (band px at scale 1). Its parallax is measured in
// lawn units at the middle of the screen, (cx - panX) / zoom, which a zoom about the centre
// leaves unchanged — so zooming scales the band about the middle, while a pan still slides it
// at its parallax share. (panX * factor alone picked up the camera's zoom-about-a-point
// offset and pushed the whole band sideways.)
export function nearBandX(o, panX, zoom, cx, factor) {
  return cx + (o - (factor * (cx - panX)) / zoom) * bandScale('near', zoom);
}

// Screen x of point `o` of any band: the near band via nearBandX, the rest (which keep their
// size) at their plain parallax share of the pan.
export function bandX(name, o, panX, zoom, cx, factor) {
  return name === 'near' ? nearBandX(o, panX, zoom, cx, factor) : o + panX * factor;
}

// The trees along the front hedge (display px at zoom 1; they scale with the hedge). Big
// enough and standing high enough that the whole canopy — the upper `canopy` share of the
// tree art — clears the hedge top, while the trunk foot stays hidden behind it.
export const NEAR_TREES = { scale: 1.6, baseUp: 50, treeH: 72, canopy: 0.75 };

// The mid band's buildings and trees repeat every `span` px, a whole number of fields-strip
// mirror periods (strip + its mirror = 2 × its width): each building then always stands on
// the same patch of painted hills, which is what lets `nudges` fix one that overhangs a hill
// edge (keys are `<texture>#<n>`, the n-th of that kind in layout order).
export const MID_OBJECTS = {
  span: 4 * BD.farm_bd_mid.w,
  nudges: { 'farm_bd_barn#0': 28, 'farm_bd_silo#0': -30 },
};

export function nudgeLayout(layout, nudges) {
  const seen = {};
  return layout.map((o) => {
    const n = seen[o.key] ?? 0;
    seen[o.key] = n + 1;
    const dx = nudges[`${o.key}#${n}`] ?? 0;
    return dx ? { ...o, x: o.x + dx } : o;
  });
}

// The one pixel-snapping rule for everything in the bands (strips, buildings, trees and their
// attachments). The game's roundPixels FLOORS a sprite's own x/y; strips were rounded: a strip
// and the house on it then disagreed by a pixel and flickered as the camera settled.
export const snapPx = (v) => Math.round(v);

// Sub-pixel motion for a whole band: the band's container is placed at `frac` (Phaser does not
// round a container's transform) and its sprites at whole pixels relative to it — so the band
// glides smoothly while its strips and objects stay pixel-locked to each other.
export function splitPx(v) {
  const base = Math.floor(v);
  return { base, frac: v - base };
}
